import { StandardEvents } from '@shopify/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} PingoDiscountBadgeRefs
 * @property {HTMLElement} badgeContent
 * @property {HTMLElement} productBadges
 * @property {HTMLElement} variantBadges
 */

/**
 * Badge row above the product title. Owns two containers:
 * - [ref="productBadges"] — static editorial badges from custom.product_badges
 * - [ref="variantBadges"] — the auto-discount pill (-XX%) for the selected
 *   variant, rebuilt from window.pingoBadgeData on variant change
 *
 * Substitution rule:
 *   selected variant on sale         → show variant container (discount pill)
 *   else product has editorial badges → show product container
 *   else                             → collapse the wrapper
 *
 * Hidden↔visible is toggled instantly via `.pingo-discount-badge--hidden`
 * (no transition — site-wide motion-strip). Pingo has no per-variant custom
 * badges (custom._pack.badges doesn't exist), so the variant container only
 * ever holds the discount pill.
 *
 * @extends {Component<PingoDiscountBadgeRefs>}
 */
class PingoDiscountBadge extends Component {
  connectedCallback() {
    super.connectedCallback();
    const section = this.closest('.shopify-section');
    if (!section) return;
    // Horizon 4.x: variant changes fire StandardEvents.productSelect (bubbles
    // up from <variant-picker>). The selected variant is delivered async via
    // event.promise; product id is on event.detail.product.id.
    section.addEventListener(StandardEvents.productSelect, this.updateBadge);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const section = this.closest('.shopify-section');
    if (section) {
      section.removeEventListener(StandardEvents.productSelect, this.updateBadge);
    }
  }

  updateBadge = (event) => {
    const productId = event.detail?.product?.id;
    if (!productId) return;

    // Combined listings can mount more than one badge block on a page;
    // only react to updates for our own product.
    if (String(productId) !== String(this.dataset.productId)) return;

    // The resolved variant arrives on event.promise (after the section fetch).
    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') return;

    promise.then(({ variant }) => {
      const variantId = variant?.id;
      if (!variantId) return;

      const variantData = window.pingoBadgeData?.[productId]?.[variantId];
      if (!variantData) return;

      const { productBadges, variantBadges } = this.refs;
      if (!productBadges || !variantBadges) return;

      const hasDiscount = Boolean(variantData.discount);
      const hasProductBadges = this.dataset.hasProductBadges === 'true';

      let targetMode = 'empty';
      if (hasDiscount) targetMode = 'variant';
      else if (hasProductBadges) targetMode = 'product';

      // Rebuild the variant container from data so markup stays authoritative
      // relative to window.pingoBadgeData.
      this.#populateVariant(variantBadges, variantData);

      productBadges.hidden = targetMode !== 'product';
      variantBadges.hidden = targetMode !== 'variant';
      this.classList.toggle('pingo-discount-badge--hidden', targetMode === 'empty');
    });
  };

  #populateVariant(container, data) {
    container.replaceChildren();
    if (data.discount) {
      container.append(this.#createPill(data.discount));
    }
  }

  // Discount pill uses the brand default colors from .pingo-pill (no inline
  // overrides) — bordeaux bg / beige text.
  #createPill(label) {
    const span = document.createElement('span');
    span.className = 'pingo-pill';
    span.textContent = label;
    return span;
  }
}

if (!customElements.get('pingo-discount-badge')) {
  customElements.define('pingo-discount-badge', PingoDiscountBadge);
}
